// src/components/Common/GooglePlacesAutocompleteNative.tsx
import React, { useEffect, useRef } from 'react';
import { FormControl, FormHelperText, InputLabel } from '@mui/material';

declare global {
  interface Window {
    google: any;
    initGooglePlacesNative?: () => void;
  }
}

interface GooglePlacesAutocompleteNativeProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (place: any) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
}

// Google API key from environment variable or config
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

// Add global styles for PAC container
const pacStyles = `
  .pac-container {
    z-index: 9999 !important;
    position: absolute !important;
  }
  .pac-item {
    cursor: pointer;
    padding: 10px;
  }
  .pac-item:hover {
    background-color: #f5f5f5;
  }
`;

export default function GooglePlacesAutocompleteNative({
  value,
  onChange,
  onPlaceSelected,
  label = "Address",
  placeholder = "",
  helperText,
  size = "small",
  fullWidth = true,
  disabled = false
}: GooglePlacesAutocompleteNativeProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    console.log('[GooglePlacesNative] Component mounted');
    
    // Inject PAC styles
    if (!document.getElementById('pac-styles')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'pac-styles';
      styleSheet.textContent = pacStyles;
      document.head.appendChild(styleSheet);
    }
    
    let autocomplete: any = null;

    const initAutocomplete = () => {
      console.log('[GooglePlacesNative] Init called');
      if (!inputRef.current || !window.google?.maps?.places?.Autocomplete) {
        console.log('[GooglePlacesNative] Not ready yet');
        return;
      }

      try {
        console.log('[GooglePlacesNative] Creating autocomplete on input:', inputRef.current);
        
        autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['establishment', 'geocode'],
          componentRestrictions: { country: 'ca' }
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          console.log('[GooglePlacesNative] Place selected:', place);
          
          if (place && place.geometry && place.formatted_address) {
            onChange(place.formatted_address);
            onPlaceSelected(place);
          }
        });

        console.log('[GooglePlacesNative] Autocomplete attached successfully');
        
        // Check for PAC container after a delay
        setTimeout(() => {
          const pacContainers = document.querySelectorAll('.pac-container');
          console.log('[GooglePlacesNative] PAC containers found:', pacContainers.length);
          pacContainers.forEach((container, index) => {
            console.log(`[GooglePlacesNative] PAC container ${index}:`, {
              display: (container as HTMLElement).style.display,
              visibility: (container as HTMLElement).style.visibility,
              zIndex: (container as HTMLElement).style.zIndex,
              innerHTML: container.innerHTML.substring(0, 100) + '...'
            });
          });
        }, 2000);
      } catch (error) {
        console.error('[GooglePlacesNative] Error:', error);
      }
    };

    // Load script if needed
    if (window.google?.maps?.places) {
      console.log('[GooglePlacesNative] Google already loaded');
      initAutocomplete();
    } else {
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (!existingScript) {
        console.log('[GooglePlacesNative] Loading Google Maps script');
        window.initGooglePlacesNative = initAutocomplete;
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&loading=async&callback=initGooglePlacesNative`;
        script.async = true;
        document.head.appendChild(script);
      } else {
        console.log('[GooglePlacesNative] Script exists, waiting...');
        window.initGooglePlacesNative = initAutocomplete;
      }
    }

    return () => {
      if (autocomplete && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, []);

  return (
    <FormControl fullWidth={fullWidth} size={size}>
      {label && <InputLabel shrink htmlFor="google-places-input" style={{ position: 'relative', transform: 'none', marginBottom: 8 }}>{label}</InputLabel>}
      <input
        ref={inputRef}
        id="google-places-input"
        type="text"
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          console.log('[GooglePlacesNative] Manual change:', e.target.value);
          onChange(e.target.value);
          
          // Force trigger autocomplete
          if (inputRef.current && window.google?.maps?.places) {
            const event = new Event('input', { bubbles: true });
            inputRef.current.dispatchEvent(event);
          }
        }}
        style={{
          width: '100%',
          padding: size === 'small' ? '8.5px 14px' : '16.5px 14px',
          fontSize: '1rem',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          outline: 'none',
          backgroundColor: disabled ? '#f5f5f5' : 'white'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#1976d2';
          console.log('[GooglePlacesNative] Focused');
        }}
        onBlur={(e) => {
          e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
          console.log('[GooglePlacesNative] Blurred');
        }}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}